import React, { useState, useEffect, useRef } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, Search, Star, ExternalLink, TrendingDown, TrendingUp,
  Store, Package, Truck, Check, Share2, Clock, Trash2, Image as ImageIcon,
  List, Plus, ShoppingCart, Heart, X, Zap, Award, DollarSign, BarChart2,
  ChevronRight, ChevronDown, Filter, RefreshCw, Tag, Percent, Sparkles,
  ArrowRight, Globe, ArrowDown
} from 'lucide-react';
import ShareModal from './ShareModal';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STORE_URLS = {
  'Amazon': 'https://www.amazon.com.mx/s?k=',
  'MercadoLibre': 'https://listado.mercadolibre.com.mx/',
  'Walmart': 'https://www.walmart.com.mx/search?q=',
  'Liverpool': 'https://www.liverpool.com.mx/tienda?s=',
  'Coppel': 'https://www.coppel.com/buscar/',
  'Best Buy': 'https://www.bestbuy.com.mx/c/search?st=',
  'Elektra': 'https://www.elektra.com.mx/search?text=',
  'Costco': 'https://www.costco.com.mx/search?text=',
  'Sams Club': 'https://www.sams.com.mx/search?utf8=%E2%9C%93&search=',
  'Office Depot': 'https://www.officedepot.com.mx/officedepot/en/search/?text='
};

const STORE_COLORS = {
  'Amazon': '#FF9900',
  'MercadoLibre': '#FFF159',
  'Walmart': '#0071CE',
  'Liverpool': '#C41230',
  'Coppel': '#005096',
  'Best Buy': '#0046BE',
  'Elektra': '#FFD700',
  'Costco': '#E31837',
  'Sams Club': '#00539B',
  'Office Depot': '#CC0000',
};

const TRENDING = [
  'iPhone 16', 'Samsung Galaxy S25', 'Nintendo Switch 2', 'PlayStation 5',
  'MacBook Air', 'AirPods Pro', 'Smart TV 55"', 'Tablet iPad', 'Xbox Series X'
];

// Reliable Pexels images by product keyword
const PRODUCT_IMAGES = {
  iphone: 'https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  phone: 'https://images.pexels.com/photos/1092644/pexels-photo-1092644.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  celular: 'https://images.pexels.com/photos/1092644/pexels-photo-1092644.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  samsung: 'https://images.pexels.com/photos/404280/pexels-photo-404280.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  laptop: 'https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  macbook: 'https://images.pexels.com/photos/812264/pexels-photo-812264.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  computadora: 'https://images.pexels.com/photos/1714208/pexels-photo-1714208.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  tv: 'https://images.pexels.com/photos/1201996/pexels-photo-1201996.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  television: 'https://images.pexels.com/photos/1201996/pexels-photo-1201996.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  playstation: 'https://images.pexels.com/photos/3945659/pexels-photo-3945659.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  xbox: 'https://images.pexels.com/photos/3945659/pexels-photo-3945659.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  nintendo: 'https://images.pexels.com/photos/3945659/pexels-photo-3945659.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  audifonos: 'https://images.pexels.com/photos/3394650/pexels-photo-3394650.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  headphones: 'https://images.pexels.com/photos/3394650/pexels-photo-3394650.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  airpods: 'https://images.pexels.com/photos/3780681/pexels-photo-3780681.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  reloj: 'https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  watch: 'https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  camara: 'https://images.pexels.com/photos/51383/photo-camera-subject-photographer-51383.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  camera: 'https://images.pexels.com/photos/51383/photo-camera-subject-photographer-51383.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  tablet: 'https://images.pexels.com/photos/1334597/pexels-photo-1334597.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  ipad: 'https://images.pexels.com/photos/1334597/pexels-photo-1334597.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
  default: 'https://images.pexels.com/photos/5632398/pexels-photo-5632398.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop',
};

const getProductImage = (name = '') => {
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(PRODUCT_IMAGES)) {
    if (key !== 'default' && lower.includes(key)) return url;
  }
  return PRODUCT_IMAGES.default;
};

const getStoreUrl = (storeName, query) => {
  const base = STORE_URLS[storeName] || 'https://www.google.com/search?q=';
  return `${base}${encodeURIComponent(query)}`;
};

// Calculate deal score 0-100 based on price vs max
const getDealScore = (price, maxPrice) => {
  if (maxPrice <= 0) return 50;
  return Math.round(100 - ((price / maxPrice) * 100));
};

const getDealLabel = (score) => {
  if (score >= 70) return { label: '🔥 Oferta', color: 'text-green-400 bg-green-500/15 border-green-500/30' };
  if (score >= 40) return { label: '✓ Buen precio', color: 'text-blue-400 bg-blue-500/15 border-blue-500/30' };
  return { label: '↑ Precio alto', color: 'text-orange-400 bg-orange-500/15 border-orange-500/30' };
};

const MarketplaceModule = ({ user }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [shareModal, setShareModal] = useState({ open: false, item: null });
  const [userLists, setUserLists] = useState([]);
  const [showAddToList, setShowAddToList] = useState({ open: false, product: null });
  const [favorites, setFavorites] = useState([]);
  const [storeFilter, setStoreFilter] = useState('all');
  const [sortBy, setSortBy] = useState('price');
  const [showFilters, setShowFilters] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    loadStores();
    loadSearchHistory();
    loadUserLists();
    loadFavorites();
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStores = async () => {
    try { const res = await axios.get(`${API}/marketplace/stores`); setStores(res.data.stores || []); } catch {}
  };
  const loadSearchHistory = async () => {
    try { const res = await axios.get(`${API}/marketplace/search-history/${user.id}`); setSearchHistory(res.data || []); } catch {}
  };
  const loadUserLists = async () => {
    try { const res = await axios.get(`${API}/lists/${user.id}`); setUserLists(res.data || []); } catch {}
  };
  const loadFavorites = async () => {
    try { const res = await axios.get(`${API}/marketplace/favorites/${user.id}`); setFavorites(res.data || []); } catch {}
  };

  const searchProduct = async (query = searchQuery) => {
    if (!query.trim()) return;
    setLoading(true);
    setResults(null);
    setStoreFilter('all');
    try {
      const res = await axios.get(`${API}/marketplace/compare?product_name=${encodeURIComponent(query)}`);
      const updated = (res.data.results || []).map(r => ({ ...r, url: getStoreUrl(r.store, query), image: getProductImage(query) }));
      setResults({ ...res.data, results: updated, query,
        best_price: res.data.best_price ? { ...res.data.best_price, url: getStoreUrl(res.data.best_price.store, query) } : null
      });
      await axios.post(`${API}/marketplace/search-history?user_id=${user.id}&query=${encodeURIComponent(query)}&results_count=${res.data.results_count}`);
      loadSearchHistory();
    } catch { toast.error('Error en la búsqueda'); }
    finally { setLoading(false); }
  };

  const addToList = async (listId, product) => {
    try {
      await axios.post(`${API}/lists/${listId}/items`, { content: `${product.name} - $${product.price} MXN (${product.store})`, product_url: product.url, product_price: product.price, product_store: product.store });
      toast.success('Agregado a la lista');
      setShowAddToList({ open: false, product: null });
    } catch { toast.error('Error al agregar'); }
  };

  const addToFavorites = async (product) => {
    try {
      await axios.post(`${API}/marketplace/favorites`, { user_id: user.id, product_name: product.name, price: product.price, store: product.store, url: product.url, image: product.image });
      toast.success('Guardado en favoritos ❤️');
      loadFavorites();
    } catch { toast.error('Error'); }
  };

  // Filtered & sorted results
  const displayedResults = results?.results ? [...results.results]
    .filter(r => storeFilter === 'all' || r.store === storeFilter)
    .sort((a, b) => sortBy === 'price' ? a.price - b.price : (b.rating || 0) - (a.rating || 0))
    : [];

  const maxPrice = results?.results ? Math.max(...results.results.map(r => r.price)) : 0;
  const minPrice = results?.results ? Math.min(...results.results.map(r => r.price)) : 0;
  const avgSaving = results?.results?.length > 1
    ? Math.round(((maxPrice - minPrice) / maxPrice) * 100)
    : 0;

  const availableStores = results?.results ? [...new Set(results.results.map(r => r.store))] : [];

  return (
      <>
      <SectionAgent section="marketplace" />
      <div className="p-3 md:p-5 max-w-4xl mx-auto">

      {/* ─── Hero Search Bar ─── */}
      <div className="relative mb-5 rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 50%, #f59e0b 100%)' }}>
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.12)', backdropFilter: 'blur(0px)' }} />
        <div className="relative p-4 md:p-6">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag size={18} className="text-white/90" />
            <h1 className="text-white force-white font-bold text-lg md:text-xl" data-testid="marketplace-title">
              Comparador de Precios
            </h1>
            <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-medium ml-auto">
              10 tiendas MX
            </span>
          </div>
          <p className="text-white/70 text-xs mb-4">Encuentra el mejor precio en tiempo real</p>

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchProduct()}
                placeholder="¿Qué producto buscas?"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none pr-10 font-medium"
                style={{ background: 'rgba(255,255,255,0.95)', color: '#0f172a' }}
                data-testid="search-input"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X size={14} style={{ color: '#94a3b8' }} />
                </button>
              )}
            </div>
            <button
              onClick={() => searchProduct()}
              disabled={loading || !searchQuery.trim()}
              className="px-4 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all disabled:opacity-60"
              style={{ background: '#0f172a', color: 'white', minWidth: 44 }}
              data-testid="search-btn"
            >
              {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
              <span className="hidden sm:inline">{loading ? 'Buscando...' : 'Comparar'}</span>
            </button>
          </div>

          {/* Trending chips */}
          <div className="mt-3 flex gap-1.5 overflow-x-auto no-scrollbar">
            {TRENDING.map(term => (
              <button
                key={term}
                onClick={() => { setSearchQuery(term); searchProduct(term); }}
                className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all hover:scale-105"
                style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Recent Searches ─── */}
      {searchHistory.length > 0 && !results && !loading && (
        <div className="glass-panel p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold flex items-center gap-2">
              <Clock size={14} className="text-red-400" />
              Búsquedas recientes
            </h3>
            <button onClick={async () => { await axios.delete(`${API}/marketplace/search-history/${user.id}`); setSearchHistory([]); }} className="text-gray-600 text-xs hover:text-red-400 transition-colors">
              Limpiar
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {searchHistory.slice(0, 8).map((item, idx) => (
              <button key={idx} onClick={() => { setSearchQuery(item.query); searchProduct(item.query); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/5 hover:bg-red-500/15 text-gray-400 hover:text-red-400 border border-white/5 hover:border-red-500/30 transition-all">
                <Clock size={10} />
                {item.query}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Loading Skeleton ─── */}
      {loading && (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="glass-panel p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-xl bg-white/10 flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-4 bg-white/10 rounded-full w-3/4 mb-2" />
                  <div className="h-3 bg-white/5 rounded-full w-1/2 mb-3" />
                  <div className="h-5 bg-white/10 rounded-full w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Results ─── */}
      {results && !loading && (
        <div className="space-y-4">

          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { icon: <Store size={14} />, label: 'Tiendas', value: results.results_count, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { icon: <TrendingDown size={14} />, label: 'Mejor precio', value: `$${(minPrice).toLocaleString()}`, color: 'text-green-400', bg: 'bg-green-500/10' },
              { icon: <Percent size={14} />, label: 'Máximo ahorro', value: `${avgSaving}%`, color: 'text-orange-400', bg: 'bg-orange-500/10' },
              { icon: <DollarSign size={14} />, label: 'Precio más alto', value: `$${(maxPrice).toLocaleString()}`, color: 'text-red-400', bg: 'bg-red-500/10' },
            ].map((s, i) => (
              <div key={i} className={`glass-panel p-3 flex items-center gap-2.5`}>
                <div className={`w-8 h-8 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0 ${s.color}`}>
                  {s.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-gray-500 text-[10px]">{s.label}</p>
                  <p className="text-white font-bold text-sm leading-tight truncate">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Best Deal Banner */}
          {results.best_price && (
            <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.08) 100%)', border: '1px solid rgba(34,197,94,0.3)' }}>
              <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={getProductImage(results.query)}
                      alt={results.query}
                      className="w-16 h-16 rounded-xl object-cover"
                      onError={e => { e.target.src = PRODUCT_IMAGES.default; }}
                    />
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <Award size={10} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <span className="text-green-400 text-xs font-semibold">🏆 Mejor Precio</span>
                    <h2 className="text-white font-bold text-xl">${results.best_price.price.toLocaleString()} <span className="text-sm font-normal text-gray-400">MXN</span></h2>
                    <p className="text-gray-400 text-xs">en <span className="text-white font-medium">{results.best_price.store}</span></p>
                  </div>
                </div>
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => addToFavorites({ name: results.query, price: results.best_price.price, store: results.best_price.store, url: results.best_price.url, image: getProductImage(results.query) })}
                    className="p-2.5 rounded-xl bg-white/5 hover:bg-pink-500/20 text-gray-400 hover:text-pink-400 transition-colors">
                    <Heart size={16} />
                  </button>
                  <button onClick={() => setShowAddToList({ open: true, product: { name: results.query, price: results.best_price.price, store: results.best_price.store, url: results.best_price.url } })}
                    className="p-2.5 rounded-xl bg-white/5 hover:bg-blue-500/20 text-gray-400 hover:text-blue-400 transition-colors">
                    <List size={16} />
                  </button>
                  <a href={results.best_price.url} target="_blank" rel="noopener noreferrer" data-testid="view-store-btn"
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm force-white transition-all hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white' }}>
                    <ExternalLink size={14} />
                    <span>Ver oferta</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Filters & Sort */}
          <div className="glass-panel p-3">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-500 text-xs">Filtrar:</span>
                {['all', ...availableStores].map(store => (
                  <button
                    key={store}
                    onClick={() => setStoreFilter(store)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                      storeFilter === store
                        ? 'bg-red-500 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {store === 'all' ? 'Todas las tiendas' : store}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-xs">Ordenar:</span>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="bg-white/5 text-gray-300 text-xs rounded-lg px-2 py-1.5 outline-none border border-white/10"
                >
                  <option value="price">Menor precio</option>
                  <option value="rating">Mejor calificación</option>
                </select>
              </div>
            </div>
          </div>

          {/* Price Comparison Cards */}
          <div className="glass-panel p-4">
            <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
              <BarChart2 size={16} className="text-red-400" />
              Comparación de precios — {displayedResults.length} resultados
            </h3>

            <div className="space-y-3">
              {displayedResults.map((result, idx) => {
                const dealScore = getDealScore(result.price, maxPrice);
                const deal = getDealLabel(dealScore);
                const barWidth = maxPrice > 0 ? Math.max(8, Math.round((result.price / maxPrice) * 100)) : 50;
                const savings = maxPrice > 0 ? maxPrice - result.price : 0;
                const isBest = idx === 0 && storeFilter === 'all';

                return (
                  <div
                    key={idx}
                    className="rounded-2xl overflow-hidden transition-all hover:scale-[1.01] cursor-default"
                    style={{
                      background: isBest ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.03)',
                      border: isBest ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(255,255,255,0.07)',
                    }}
                    data-testid={`result-item-${idx}`}
                  >
                    <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Product Image */}
                      <div className="flex-shrink-0 relative">
                        {isBest && (
                          <div className="absolute -top-1 -left-1 z-10 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <Check size={10} className="text-white" />
                          </div>
                        )}
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5">
                          <img
                            src={imageErrors[`${idx}`] ? PRODUCT_IMAGES.default : getProductImage(result.product_name || results.query)}
                            alt={result.product_name}
                            className="w-full h-full object-cover"
                            onError={() => setImageErrors(prev => ({ ...prev, [`${idx}`]: true }))}
                          />
                        </div>
                      </div>

                      {/* Store + Rating */}
                      <div className="sm:w-32 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STORE_COLORS[result.store] || '#888' }} />
                          <p className="text-white text-sm font-semibold truncate">{result.store}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star size={10} className="text-yellow-400 fill-yellow-400" />
                          <span className="text-gray-500 text-xs">{result.rating}</span>
                          {result.in_stock ? (
                            <span className="text-green-400 text-[10px] ml-1">● En stock</span>
                          ) : (
                            <span className="text-red-400 text-[10px] ml-1">● Agotado</span>
                          )}
                        </div>
                      </div>

                      {/* Price + Bar */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className={`text-xl font-bold ${isBest ? 'text-green-400' : 'text-white'}`}>
                            ${result.price.toLocaleString()}
                          </span>
                          <span className="text-gray-500 text-xs">MXN</span>
                          {savings > 0 && (
                            <span className="text-xs text-green-400 ml-1">−${savings.toLocaleString()} vs caro</span>
                          )}
                        </div>
                        {/* Visual price bar */}
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${barWidth}%`,
                              background: isBest
                                ? 'linear-gradient(90deg, #16a34a, #22c55e)'
                                : barWidth < 50
                                ? 'linear-gradient(90deg, #2563eb, #3b82f6)'
                                : 'linear-gradient(90deg, #ea580c, #f97316)'
                            }}
                          />
                        </div>
                        <p className="text-gray-600 text-[10px] mt-0.5">{result.shipping}</p>
                      </div>

                      {/* Deal Badge + Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${deal.color}`}>
                          {deal.label}
                        </span>
                        <button onClick={() => setShowAddToList({ open: true, product: { name: result.product_name || results.query, price: result.price, store: result.store, url: result.url } })}
                          className="p-2 text-gray-600 hover:text-blue-400 hover:bg-blue-500/15 rounded-lg transition-colors">
                          <List size={14} />
                        </button>
                        <a href={result.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                          style={{ background: '#dc2626', color: 'white' }}>
                          <ExternalLink size={12} />
                          Ver
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Savings Summary */}
          {avgSaving > 0 && (
            <div className="rounded-2xl p-4 flex items-center gap-4"
              style={{ background: 'linear-gradient(135deg, rgba(234,88,12,0.1) 0%, rgba(245,158,11,0.08) 100%)', border: '1px solid rgba(234,88,12,0.25)' }}>
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles size={18} className="text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-orange-400 text-xs font-semibold mb-0.5">Máximo ahorro potencial</p>
                <p className="text-white text-sm">
                  Comprar en <strong>{results.best_price?.store}</strong> te ahorra{' '}
                  <strong className="text-orange-400">${(maxPrice - minPrice).toLocaleString()} MXN ({avgSaving}%)</strong>{' '}
                  vs la tienda más cara
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Connected Stores (no results yet) ─── */}
      {!results && !loading && (
        <div className="glass-panel p-4">
          <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
            <Globe size={14} className="text-red-400" />
            Tiendas conectadas
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {(stores.length > 0 ? stores : Object.keys(STORE_URLS).map(n => ({ id: n, name: n, connected: true }))).map(store => (
              <div key={store.id || store.name}
                className="rounded-xl p-3 text-center transition-all hover:scale-105 cursor-pointer"
                style={{ background: store.connected ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)', border: `1px solid ${store.connected ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.07)'}` }}
                onClick={() => inputRef.current?.focus()}
              >
                <div className="w-2 h-2 rounded-full mx-auto mb-1.5" style={{ background: store.connected ? '#22c55e' : '#6b7280' }} />
                <p className="text-white text-[10px] font-medium truncate">{store.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Add to List Modal ─── */}
      {showAddToList.open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2"><List className="text-red-400" size={18} />Agregar a Lista</h3>
              <button onClick={() => setShowAddToList({ open: false, product: null })} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-gray-400 text-sm mb-4 line-clamp-2">{showAddToList.product?.name} — ${showAddToList.product?.price?.toLocaleString()} MXN</p>
            {userLists.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm">No tienes listas. Crea una en la sección de Listas.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {userLists.map(list => (
                  <button key={list.id} onClick={() => addToList(list.id, showAddToList.product)}
                    className="w-full p-3 bg-white/5 hover:bg-red-500/10 rounded-xl text-left flex items-center gap-3 transition-colors">
                    <List size={16} className="text-red-400" />
                    <span className="text-white text-sm">{list.name}</span>
                    <span className="text-gray-500 text-xs ml-auto">{list.items?.length || 0} items</span>
                    <ChevronRight size={14} className="text-gray-600" />
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowAddToList({ open: false, product: null })} className="mt-4 w-full btn-secondary">Cancelar</button>
          </div>
        </div>
      )}

      <ShareModal
        isOpen={shareModal.open}
        onClose={() => setShareModal({ open: false, item: null })}
        itemType={shareModal.item?.type || 'product'}
        itemId={shareModal.item?.url || ''}
        itemTitle={shareModal.item?.name || 'Producto'}
        itemData={{ userId: user.id, ...shareModal.item }}
      />

      <AIAgentModule sectionContext="marketplace y comparación de precios" compact={true} placeholder="¿Qué producto buscas? Te ayudo a comparar precios..." />
    </div>
  
      </>
    );
};

export default MarketplaceModule;
